import { useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import legalIdStyles from "../legalIdVerification/LegalIdVerification.module.scss";
import styles from "./AddressHistory.module.scss";
import { logger } from "../../shared/utils/logger";
import { useConsumer } from "../ConsumerProvider";
import type { AddressCandidate } from "../../sdk/iframeAddressHistory";
// VEX-7420: the confirm/deny rule + its selection-mode fail-safe live in a
// pure module (decisions.ts), tested in isolation. The component owns the
// UI state; the module owns the polarity.
import {
  coerceSelectionMode,
  computeAddressDecisions,
  type Address,
  type SelectionMode,
} from "./decisions";

// VEX-7323: the SDK consumes the canonical AddressCandidate shape (six
// required idme-data adapter fields + address_uuid). Component-internal
// references use the ApiAddress alias to keep call sites stable.
type ApiAddress = AddressCandidate;

type VerificationState = "initializing" | "ready" | "processing" | "completed" | "error";
type ViewMode = "view" | "manage";
type Banner = { type: "success"; count: number } | { type: "error" } | null;

function formatAddress(a: ApiAddress): string {
  const street = [a.street1, a.street2].filter(Boolean).join(" ").trim();
  const stateZip = [a.state, a.zip].filter(Boolean).join(" ").trim();
  return [street, a.city, stateZip].filter(Boolean).join(", ");
}

// Fallback candidates used in standalone/mock mode when no candidates are
// injected via IDME_INIT (e.g., loading the iframe directly for design QA).
// Each entry satisfies the canonical AddressCandidate shape (six fields
// required by idme-data's Claim::Adapter::Address adapter).
const MOCK_CANDIDATES: Address[] = [
  { street1: "742 Evergreen Terrace", street2: null, city: "Springfield", state: "IL", zip: "62701" },
  { street1: "1234 Oak Avenue", street2: "Apt 5B", city: "Portland", state: "OR", zip: "97201" },
  { street1: "567 Maple Street", street2: null, city: "Austin", state: "TX", zip: "78701" },
  { street1: "890 Pine Ridge Drive", street2: null, city: "Denver", state: "CO", zip: "80202" },
  { street1: "231 Green Road", street2: null, city: "Hotfield", state: "IL", zip: "32432" },
  { street1: "342 Birchwood Lane", street2: null, city: "Denver", state: "CO", zip: "80203" },
  { street1: "719 Harbor View Drive", street2: null, city: "Portland", state: "OR", zip: "97201" },
  { street1: "156 Magnolia Court", street2: null, city: "Nashville", state: "TN", zip: "37201" },
  { street1: "483 Cedarwood Avenue", street2: null, city: "Buffalo", state: "NY", zip: "14201" },
  { street1: "827 Stonegate Boulevard", street2: null, city: "Tucson", state: "AZ", zip: "85701" },
].map((fields, idx) => {
  const raw: ApiAddress = {
    address_uuid: `mock-${idx}`,
    street1: fields.street1,
    street2: fields.street2,
    city: fields.city,
    state: fields.state,
    country: "US",
    zip: fields.zip,
  };
  return { uuid: raw.address_uuid, display: formatAddress(raw), raw };
});

function LogoHeader() {
  // VEX-7983: with the Fragno logo on, BrandLayout renders the shared header instead.
  const { fragnoLogoEnabled } = useConsumer();
  if (fragnoLogoEnabled) return null;
  return (
    <div className={legalIdStyles.logoContainer}>
      <div className={legalIdStyles.logo}>
        <img src="/assets/svg/idme-logo.svg" alt="IDMe" />
      </div>
    </div>
  );
}

export function AddressHistory() {
  const { t } = useTranslation();
  const [state, setState] = useState<VerificationState>("initializing");
  const [mode, setMode] = useState<ViewMode>("view");
  const [selectionMode, setSelectionMode] = useState<SelectionMode>("confirm_by_default");
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  // VEX-7420 (opt-in): addresses the user has affirmatively selected to
  // `confirm`. Keyed by uuid so submit stays correct regardless of list order.
  const [selectedUuids, setSelectedUuids] = useState<Set<string>>(new Set());
  const [banner, setBanner] = useState<Banner>(null);
  const bannerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bannerKey = useRef(0);
  const [parentOrigin, setParentOrigin] = useState<string | null>(null);
  // VEX-7314 (Abhishek reshape 2026-05-21): candidate addresses arrive
  // verbatim via the IDME_INIT postMessage payload — the consuming app
  // (IVA) hydrates them server-side. No HTTP read from the iframe.
  // Decisions still ship out via VERIFICATION_COMPLETE.
  // Preserve the original candidate set so the on-submit decision payload
  // can include `deny` entries for removed addresses (the UI list mutates
  // on removal).
  const originalAddresses = useRef<Address[]>([]);
  const removedUuids = useRef<Set<string>>(new Set());

  const hydrateFromCandidates = useCallback((raw: ApiAddress[] | undefined) => {
    const hydrated: Address[] = (raw ?? []).map((a: ApiAddress) => ({
      uuid: a.address_uuid,
      display: formatAddress(a),
      raw: a,
    }));
    if (hydrated.length === 0) {
      logger.warn("[AddressHistory] No candidates in IDME_INIT, falling back to mock");
      originalAddresses.current = MOCK_CANDIDATES;
      setAddresses(MOCK_CANDIDATES);
    } else {
      originalAddresses.current = hydrated;
      setAddresses(hydrated);
    }
    setState("ready");
  }, []);

  useEffect(() => {
    try {
      if (!document.referrer) {
        logger.log("[AddressHistory] Standalone mode - no parent origin");
        // VEX-7323 self-review: seed originalAddresses so handleSubmit doesn't
        // emit decisions:[] if the user reaches the standalone submit button.
        originalAddresses.current = MOCK_CANDIDATES;
        setAddresses(MOCK_CANDIDATES);
        setState("ready");
        return;
      }

      const origin = new URL(document.referrer).origin;
      setParentOrigin(origin);

      window.parent.postMessage(
        { source: "idme-verification", type: "IDME_READY" },
        origin
      );

      logger.log("[AddressHistory] IDME_READY sent to parent, origin:", origin);

      const handleInitMessage = (event: MessageEvent) => {
        // VEX-7323 self-review: reject IDME_INIT from any origin other than
        // the captured parent. Mirrors the SDK-side check at
        // iframeAddressHistory.ts:76 — prevents arbitrary candidate injection
        // from any window that has a reference to this iframe.
        if (event.origin !== origin) return;
        if (event.data?.type !== "IDME_INIT") return;
        logger.log("[AddressHistory] Received IDME_INIT:", event.data.payload);
        window.removeEventListener("message", handleInitMessage);

        // VEX-7420: resolve selection mode (opt-in vs opt-out) from the
        // payload, defaulting to opt-out for any absent/unrecognized value.
        setSelectionMode(coerceSelectionMode(event.data.payload?.selectionMode));

        const candidates: ApiAddress[] | undefined = event.data.payload?.candidates;
        hydrateFromCandidates(candidates);
      };

      window.addEventListener("message", handleInitMessage);

      return () => {
        window.removeEventListener("message", handleInitMessage);
      };
    } catch (error) {
      logger.error("[AddressHistory] Initialization error:", error);
      // VEX-7323 self-review: seed originalAddresses in the error-fallback
      // path so a degraded init doesn't silently submit decisions:[] if
      // parentOrigin happened to land before the throw.
      originalAddresses.current = MOCK_CANDIDATES;
      setAddresses(MOCK_CANDIDATES);
      setState("ready");
    }
  }, [hydrateFromCandidates]);

  const showBanner = useCallback((next: Exclude<Banner, null>) => {
    if (bannerTimer.current) clearTimeout(bannerTimer.current);
    bannerKey.current += 1;
    setBanner(next);
    bannerTimer.current = setTimeout(() => setBanner(null), 5000);
  }, []);

  const handleSubmit = useCallback(() => {
    logger.log("[AddressHistory] Submitting address history");
    setState("processing");

    // VEX-7314 (event-pattern reshape): emit the rich decision set in a
    // single VERIFICATION_COMPLETE postMessage. The host's Stimulus
    // controller forwards this payload as
    // `{ address_history_websdk_result: { decisions: [...] } }` to IVA's
    // update action; IVA writes the confirmed subset to Redis.
    // VEX-7420: the active mode determines which UI set holds the user's
    // toggles; computeAddressDecisions owns the polarity (touched → confirm in
    // opt-in, → deny in opt-out). Always passes the ORIGINAL candidate set so
    // removed addresses ship as explicit `deny` rather than vanishing.
    const touchedUuids = selectionMode === "deny_by_default" ? selectedUuids : removedUuids.current;
    const decisions = computeAddressDecisions({
      selectionMode,
      candidates: originalAddresses.current,
      touchedUuids,
    });

    if (parentOrigin) {
      window.parent.postMessage(
        {
          source: "idme-verification",
          type: "VERIFICATION_COMPLETE",
          payload: { decisions },
        },
        parentOrigin
      );
      logger.log("[AddressHistory] Sent VERIFICATION_COMPLETE to parent");
    }
  }, [parentOrigin, selectionMode, selectedUuids]);

  // VEX-7420 (opt-in): toggle an address into/out of the confirmed selection.
  const handleToggleSelect = useCallback((uuid: string) => {
    setSelectedUuids(prev => {
      const next = new Set(prev);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }
      return next;
    });
  }, []);

  const handleManageAddresses = useCallback(() => {
    setMode("manage");
    setSelected(new Set());
  }, []);

  const handleCancelManage = useCallback(() => {
    setMode("view");
    setSelected(new Set());
  }, []);

  const handleToggleAddress = useCallback((index: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  const handleRemoveAddresses = useCallback(() => {
    if (selected.size === 0) {
      setMode("view");
      setSelected(new Set());
      showBanner({ type: "error" });
      return;
    }
    const count = selected.size;
    logger.log("[AddressHistory] Removing addresses:", Array.from(selected));

    // VEX-7314 (event-pattern reshape): track removed addresses locally;
    // their `deny` decisions ship in the rich onComplete payload at submit.
    const toRemove = addresses.filter((_, i) => selected.has(i));
    toRemove.forEach(a => removedUuids.current.add(a.uuid));

    setAddresses(prev => prev.filter((_, i) => !selected.has(i)));
    setMode("view");
    setSelected(new Set());
    showBanner({ type: "success", count });
  }, [selected, addresses, showBanner]);

  if (state === "initializing") {
    return (
      <div className={legalIdStyles.pageContainer}>
        <div className={legalIdStyles.container}>
          <div className={legalIdStyles.contentContainer}>
            <LogoHeader />
            <div className={legalIdStyles.card}>
              <div className={legalIdStyles.spinner} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (state === "processing") {
    return (
      <div className={legalIdStyles.pageContainer}>
        <div className={legalIdStyles.container}>
          <div className={legalIdStyles.contentContainer}>
            <LogoHeader />
            <div className={legalIdStyles.card}>
              <h2 className={legalIdStyles.processingTitle}>
                {t("addressHistory.processing.title", "Processing...")}
              </h2>
              <div className={legalIdStyles.processingSpinner} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (state === "completed") {
    return (
      <div className={legalIdStyles.pageContainer}>
        <div className={legalIdStyles.container}>
          <div className={legalIdStyles.contentContainer}>
            <LogoHeader />
            <div className={legalIdStyles.card}>
              <div className={legalIdStyles.successIcon}>✓</div>
              <h2 className={legalIdStyles.title}>
                {t("addressHistory.completed.title", "Address history submitted")}
              </h2>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className={legalIdStyles.pageContainer}>
        <div className={legalIdStyles.container}>
          <div className={legalIdStyles.contentContainer}>
            <LogoHeader />
            <div className={legalIdStyles.card}>
              <div className={legalIdStyles.errorIcon}>✕</div>
              <h2 className={legalIdStyles.title}>
                {t("addressHistory.error.title", "Verification failed")}
              </h2>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // VEX-7420 (opt-in): a single always-visible checkbox list, all unchecked by
  // default. No manage sub-flow, no add/edit. Selected = confirm; the rest deny.
  if (selectionMode === "deny_by_default") {
    return (
      <div className={styles.page}>
        <div className={styles.inner}>
          <LogoHeader />

          <h1 className={styles.pageTitle}>
            {t("addressHistory.list.title", "Review and confirm your address history")}
          </h1>

          <p className={styles.subtitle}>
            {t(
              "addressHistory.list.optInSubtitle",
              "We found these addresses in authoritative sources. Select all addresses you've lived at so we can match you with the right records and info."
            )}
          </p>

          {addresses.length === 0 ? (
            <div className={styles.ctaArea}>
              <button
                type="button"
                className={legalIdStyles.openCameraButton}
                onClick={handleSubmit}
              >
                {t("addressHistory.list.continueWithout", "Continue without addresses")}
              </button>
              <p className={styles.warningText}>
                {t(
                  "addressHistory.list.continueWithoutWarning",
                  "Continuing without addresses may cause delays in accessing services."
                )}
              </p>
            </div>
          ) : (
            <>
              <p className={styles.addressCount}>
                {t("addressHistory.list.selectedCount", "{{count}} of {{total}} addresses selected", {
                  count: selectedUuids.size,
                  total: addresses.length,
                })}
              </p>

              <ul
                className={styles.addressList}
                aria-label={t("addressHistory.list.ariaLabel", "Your address history")}
              >
                {addresses.map((address, index) => {
                  const isFirst = index === 0;
                  const isLast = index === addresses.length - 1;
                  const isChecked = selectedUuids.has(address.uuid);

                  const itemClass = [
                    styles.addressItem,
                    isFirst ? styles.addressItemFirst : "",
                    isLast ? styles.addressItemLast : "",
                  ]
                    .filter(Boolean)
                    .join(" ");

                  return (
                    <li key={address.uuid} className={itemClass}>
                      <label className={styles.addressItemLabel}>
                        <input
                          type="checkbox"
                          className={styles.checkboxInput}
                          checked={isChecked}
                          onChange={() => handleToggleSelect(address.uuid)}
                          aria-label={address.display}
                        />
                        <span
                          className={[styles.checkboxVisual, isChecked ? styles.checkboxChecked : ""]
                            .filter(Boolean)
                            .join(" ")}
                          aria-hidden="true"
                        />
                        <span className={styles.addressText}>{address.display}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>

              <div className={styles.ctaArea}>
                <button
                  type="button"
                  className={legalIdStyles.openCameraButton}
                  onClick={handleSubmit}
                >
                  {t("addressHistory.list.submit", "Submit address history")}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  const isManaging = mode === "manage";

  return (
    <div className={styles.page}>
      <div className={styles.inner}>
        <LogoHeader />

        <h1 className={styles.pageTitle}>
          {t("addressHistory.list.title", "Review and confirm your address history")}
        </h1>

        <p className={styles.subtitle}>
          {t(
            "addressHistory.list.subtitle",
            "We found these addresses in authoritative sources. Confirm your history so we can match you with the right records and info."
          )}
        </p>

        <p className={styles.addressCount}>
          {isManaging
            ? t("addressHistory.list.countManage", "{{count}} addresses", { count: addresses.length })
            : t("addressHistory.list.count", "{{count}} addresses found", { count: addresses.length })}
        </p>

        {isManaging ? (
          <p className={styles.manageSubtitle}>
            {t("addressHistory.list.manageSubtitle", "Select any addresses where you never lived.")}
          </p>
        ) : addresses.length > 0 ? (
          <p className={styles.manageRow}>
            {t("addressHistory.list.notAllYours", "Not all yours?")}{" "}
            <button type="button" className={styles.manageLink} onClick={handleManageAddresses}>
              {t("addressHistory.list.manageAddresses", "Manage addresses")}
            </button>
          </p>
        ) : null}

        {banner !== null && (
          banner.type === "error" ? (
            <div key={bannerKey.current} className={styles.selectErrorBanner} role="alert">
              <svg
                aria-hidden="true"
                className={styles.successBannerIcon}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="10" cy="10" r="9" stroke="#6b7280" strokeWidth="1.5" />
                <path d="M10 6v4m0 3v.5" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <span>
                {t("addressHistory.list.noSelection", "No addresses were removed.")}
              </span>
            </div>
          ) : (
            <div key={bannerKey.current} className={styles.successBanner} role="status">
              <svg
                aria-hidden="true"
                className={styles.successBannerIcon}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="10" cy="10" r="9" stroke="#095C37" strokeWidth="1.5" />
                <path d="M6 10l3 3 5-5" stroke="#095C37" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>
                {banner.count === 1
                  ? t("addressHistory.list.removedSingle", "1 address was removed.")
                  : t("addressHistory.list.removedPlural", "{{count}} addresses were removed.", { count: banner.count })}
              </span>
            </div>
          )
        )}

        <ul
          className={styles.addressList}
          aria-label={t("addressHistory.list.ariaLabel", "Your address history")}
        >
          {addresses.map((address, index) => {
            const isFirst = index === 0;
            const isLast = index === addresses.length - 1;
            const isChecked = selected.has(index);

            const itemClass = [
              styles.addressItem,
              isFirst ? styles.addressItemFirst : "",
              isLast ? styles.addressItemLast : "",
              isChecked ? styles.addressItemChecked : "",
            ]
              .filter(Boolean)
              .join(" ");

            if (isManaging) {
              return (
                <li key={address.uuid} className={itemClass}>
                  <label className={styles.addressItemLabel}>
                    <input
                      type="checkbox"
                      className={styles.checkboxInput}
                      checked={isChecked}
                      onChange={() => handleToggleAddress(index)}
                      aria-label={address.display}
                    />
                    <span
                      className={[styles.checkboxVisual, isChecked ? styles.checkboxChecked : ""].filter(Boolean).join(" ")}
                      aria-hidden="true"
                    />
                    <span className={[styles.addressText, isChecked ? styles.addressTextChecked : ""].filter(Boolean).join(" ")}>{address.display}</span>
                  </label>
                </li>
              );
            }

            return (
              <li key={address.uuid} className={itemClass}>
                {address.display}
              </li>
            );
          })}
        </ul>

        <div className={styles.ctaArea}>
          {isManaging ? (
            <>
              <button
                type="button"
                className={legalIdStyles.openCameraButton}
                onClick={handleRemoveAddresses}
              >
                {selected.size > 0
                  ? t("addressHistory.list.removeCount", "Remove {{count}} addresses", { count: selected.size })
                  : t("addressHistory.list.removeAddresses", "Remove addresses")}
              </button>
              <button type="button" className={styles.cancelButton} onClick={handleCancelManage}>
                {t("addressHistory.list.cancel", "Cancel")}
              </button>
            </>
          ) : (
            <>
              {addresses.length === 0 ? (
                <>
                  <button
                    type="button"
                    className={legalIdStyles.openCameraButton}
                    onClick={handleSubmit}
                  >
                    {t("addressHistory.list.continueWithout", "Continue without addresses")}
                  </button>
                  <p className={styles.warningText}>
                    {t(
                      "addressHistory.list.continueWithoutWarning",
                      "Continuing without addresses may cause delays in accessing services."
                    )}
                  </p>
                </>
              ) : (
                <button
                  type="button"
                  className={legalIdStyles.openCameraButton}
                  onClick={handleSubmit}
                >
                  {t("addressHistory.list.submit", "Submit address history")}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
